import { ref, type Ref } from "vue";
import { CustomerAdditionalInfoType } from "@/shared/@types/CustomerAdditionalInfoType";
import { getCustomer } from "@/shared/services/getCustomer.service";
import type { CustomerBase } from "@/shared/@types/CustomerBase";
import { type Customer, mapToCustomerType } from "@/shared/@types/Customer";

export const useGetCustomer = (
  customerBase?: Ref<CustomerBase | undefined>,
) => {
  const customer = ref<Customer | undefined>(undefined);
  const isFetchingCustomer = ref(false);
  const fetchingCustomerError = ref<string | undefined>(undefined);

  // As this composable is called from stores as well, we do not want to use the store's user and customerId
  const onFetchingCustomer = async ({
    userId,
    customerId,
    throwError = false,
  }: {
    userId: string;
    customerId: string;
    throwError?: boolean;
  }) => {
    try {
      fetchingCustomerError.value = undefined;
      isFetchingCustomer.value = true;

      const result = await getCustomer({
        userId,
        customerId,
        // Add other types separated by ',' to get more info
        // without CustomerAdditionalInfoType.Addresses, because addresses need to be fetched in a separate composable
        infoTypes: [CustomerAdditionalInfoType.ContactAddresses],
      });
      customer.value = mapToCustomerType(result, customerBase?.value);
    } catch (error) {
      if (throwError) throw error;
      fetchingCustomerError.value = error as string;
    } finally {
      isFetchingCustomer.value = false;
    }
  };

  return {
    customer,
    isFetchingCustomer,
    fetchingCustomerError,
    onFetchingCustomer,
  };
};
